/**
 * Variant-level filtering for collection pages that display variants as individual cards.
 * Handles Availability filtering (extending Shopify's native filter) and
 * N dynamic filters (read from data-variant-filters JSON config on the grid).
 *
 * Filter configs come from Theme Editor blocks (type: variant_filter).
 * Each block specifies a metafield key, a display label, and whether to show color swatches.
 */

class VariantAvailabilityFilter {
  constructor() {
    this.variantGridSection = document.querySelector('.variant-product-grid-section');
    if (!this.variantGridSection) return;

    this.productGrid = this.variantGridSection.querySelector('#product-grid');
    if (!this.productGrid) return;

    this._lastFilterTime = 0;

    // Color slug → hex mapping for swatch display
    this.colorHexMap = {
      'black-smoke': '#1a1a1a',
      'black': '#1a1a1a',
      'blue': '#2979ff',
      'gold-yellow': '#d4a017',
      'gold': '#d4a017',
      'green': '#2e7d32',
      'orange': '#ef6c00',
      'pink': '#e91e8a',
      'purple': '#7b1fa2',
      'red': '#d32f2f',
      'white': '#ffffff',
      'clear': '#e0e8ef',
      'silver': '#b0b0b0',
      'brown': '#6d4c41',
      'yellow': '#f9a825',
      'grey': '#757575',
      'gray': '#757575',
      'teal': '#00897b',
      'navy': '#1a237e',
      'maroon': '#7b1c2a',
    };

    // Read filter configs from the grid's data attribute
    this.filterConfigs = this.readFilterConfigs();

    this.init();
  }

  readFilterConfigs() {
    try {
      const raw = this.productGrid.getAttribute('data-variant-filters');
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // Silently fail — no filters configured
    }
    return [];
  }

  init() {
    if (typeof FacetFiltersForm === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.originalRenderProductGridContainer = FacetFiltersForm.renderProductGridContainer;
    const self = this;
    FacetFiltersForm.renderProductGridContainer = function(html) {
      self.originalRenderProductGridContainer.call(this, html);
      setTimeout(() => {
        self.productGrid = self.variantGridSection.querySelector('#product-grid');
        if (self.productGrid) {
          self.filterConfigs = self.readFilterConfigs();
          self.buildCustomFilters();
          self.applyVariantFiltering();
        }
      }, 300);
    };

    this.buildCustomFilters();
    this.applyVariantFiltering();

    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const gridObserver = new MutationObserver(() => {
        const now = Date.now();
        if (now - this._lastFilterTime < 200) return;
        setTimeout(() => {
          this.filterConfigs = this.readFilterConfigs();
          this.buildCustomFilters();
          this.applyVariantFiltering();
        }, 300);
      });
      gridObserver.observe(gridContainer, {
        childList: true,
        subtree: true
      });
    }
  }

  // ── Color Swatches ──────────────────────────────────────────────

  getColorHex(slug) {
    if (this.colorHexMap[slug]) return this.colorHexMap[slug];
    for (const [key, hex] of Object.entries(this.colorHexMap)) {
      if (slug.includes(key) || key.includes(slug)) return hex;
    }
    return null;
  }

  // ── Generic Filter UI ──────────────────────────────────────────

  buildCustomFilters() {
    // Remove all existing custom filters
    this.variantGridSection.querySelectorAll('.variant-custom-filter').forEach((el) => el.remove());

    if (!this.filterConfigs.length) return;

    const facetsWrapper = this.variantGridSection.querySelector('#FacetsWrapperDesktop');
    if (!facetsWrapper) return;

    for (const config of this.filterConfigs) {
      this.buildSingleFilter(config, facetsWrapper);
    }
  }

  buildSingleFilter(config, container) {
    const attrKey = `data-variant-filter-${config.key}`;
    const attrLabelKey = `${attrKey}-label`;
    const variantItems = this.productGrid.querySelectorAll(`li.grid__item[${attrKey}]`);
    const valueMap = new Map();

    variantItems.forEach((item) => {
      const slug = item.getAttribute(attrKey);
      const label = item.getAttribute(attrLabelKey);
      if (!slug || !label) return;

      if (valueMap.has(slug)) {
        valueMap.get(slug).count++;
      } else {
        valueMap.set(slug, { label, count: 1 });
      }
    });

    if (valueMap.size === 0) return;

    const prevSelected = this.getSelectedValues(config.key);

    const sorted = [...valueMap.entries()].sort((a, b) =>
      a[1].label.localeCompare(b[1].label)
    );

    const checkboxSvg = '<svg width="1.6rem" height="1.6rem" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect></svg>';
    const checkmarkSvg = '<svg aria-hidden="true" class="icon icon-checkmark" width="1.1rem" height="0.7rem" viewBox="0 0 11 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" /></svg>';
    const caretSvg = '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></svg>';

    let listItems = '';
    sorted.forEach(([slug, { label, count }], i) => {
      const checked = prevSelected.includes(slug) ? 'checked' : '';
      let swatchHtml = '';
      if (config.isColor) {
        const hex = this.getColorHex(slug);
        if (hex) {
          const isLight = ['#ffffff', '#e0e8ef', '#f9a825'].includes(hex);
          swatchHtml = `<span class="color-swatch${isLight ? ' color-swatch--light' : ''}" style="background-color: ${hex};"></span>`;
        }
      }

      listItems += `
        <li class="list-menu__item facets__item">
          <label for="Filter-variant-${config.key}-${i}" class="facets__label facet-checkbox${checked ? ' active' : ''}">
            <input type="checkbox" id="Filter-variant-${config.key}-${i}" value="${slug}" data-custom-filter="${config.key}" ${checked}>
            ${checkboxSvg}
            ${checkmarkSvg}
            ${swatchHtml}
            <span class="facet-checkbox__text" aria-hidden="true">${label} (${count})</span>
          </label>
        </li>`;
    });

    const hasActive = prevSelected.length > 0;
    const html = `
      <details class="facets__disclosure-vertical variant-custom-filter" data-filter-key="${config.key}" open>
        <summary class="facets__summary caption-large focus-offset">
          <div>
            <span class="facets__summary-label">
              ${config.label}
              <span class="facets__selected no-js-hidden${hasActive ? '' : ' hidden'}" data-filter-count="${config.key}">(${prevSelected.length})</span>
            </span>
            ${caretSvg}
          </div>
        </summary>
        <div class="facets__display-vertical">
          <fieldset class="facets-wrap facets-wrap-vertical">
            <legend class="visually-hidden">${config.label}</legend>
            <ul class="facets-layout-list facets__list--vertical list-unstyled no-js-hidden" role="list">
              ${listItems}
            </ul>
            <div class="facets__reset-wrapper${hasActive ? '' : ' hidden'}" data-filter-reset-wrapper="${config.key}">
              <a href="javascript:void(0)" class="facets__reset link underlined-link" data-filter-reset="${config.key}">Clear</a>
            </div>
          </fieldset>
        </div>
      </details>`;

    container.insertAdjacentHTML('beforeend', html);

    // Bind checkbox change events — stop propagation to prevent Shopify's
    // facets.js from treating these as native filter changes and doing an AJAX reload
    this.variantGridSection.querySelectorAll(`[data-custom-filter="${config.key}"]`).forEach((input) => {
      input.addEventListener('input', (e) => e.stopPropagation());
      input.addEventListener('change', () => this.onCustomFilterChange(config.key));
    });

    // Bind clear link
    const resetBtn = this.variantGridSection.querySelector(`[data-filter-reset="${config.key}"]`);
    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.variantGridSection.querySelectorAll(`[data-custom-filter="${config.key}"]:checked`).forEach((cb) => {
          cb.checked = false;
          cb.closest('.facets__label').classList.remove('active');
        });
        this.onCustomFilterChange(config.key);
      });
    }
  }

  onCustomFilterChange(filterKey) {
    this.variantGridSection.querySelectorAll(`[data-custom-filter="${filterKey}"]`).forEach((input) => {
      input.closest('.facets__label').classList.toggle('active', input.checked);
    });

    const selected = this.getSelectedValues(filterKey);
    const countEl = this.variantGridSection.querySelector(`[data-filter-count="${filterKey}"]`);
    if (countEl) {
      countEl.textContent = `(${selected.length})`;
      countEl.classList.toggle('hidden', selected.length === 0);
    }

    const resetWrapper = this.variantGridSection.querySelector(`[data-filter-reset-wrapper="${filterKey}"]`);
    if (resetWrapper) {
      resetWrapper.classList.toggle('hidden', selected.length === 0);
    }

    this.applyVariantFiltering();
  }

  getSelectedValues(filterKey) {
    const checked = this.variantGridSection.querySelectorAll(`[data-custom-filter="${filterKey}"]:checked`);
    return Array.from(checked).map((cb) => cb.value);
  }

  // ── Availability Filter ──────────────────────────────────────────

  findAvailabilityFilter() {
    const allFilters = this.variantGridSection.querySelectorAll('.facets__disclosure, .facets__disclosure-vertical, .mobile-facets__details');
    for (const filter of allFilters) {
      if (filter.classList.contains('variant-custom-filter')) continue;
      const summary = filter.querySelector('.facets__summary, .mobile-facets__summary');
      if (summary) {
        const labelText = summary.textContent.toLowerCase();
        if (labelText.includes('availability') || labelText.includes('stock')) {
          return filter;
        }
      }
    }
    return null;
  }

  getAvailabilityFilterValue() {
    const activeFacets = this.variantGridSection.querySelectorAll('.active-facets__button');
    for (const facet of activeFacets) {
      const text = (facet.textContent || '').toLowerCase();
      if (text.includes('availability') || text.includes('stock')) {
        if (text.includes('in stock') || text.includes('available')) {
          return '1';
        } else if (text.includes('out of stock') || text.includes('unavailable')) {
          return '0';
        }
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    for (const [key, value] of urlParams.entries()) {
      if (key.includes('availability')) {
        if (key.endsWith('.1') || value === '1') {
          return '1';
        } else if (key.endsWith('.0') || value === '0') {
          return '0';
        }
        return value;
      }
    }

    const availabilityFilter = this.findAvailabilityFilter();
    if (availabilityFilter) {
      const checkedInputs = availabilityFilter.querySelectorAll('input[type="checkbox"]:checked');
      for (const input of checkedInputs) {
        const value = input.value;
        const name = input.name || '';
        if (name.includes('.1') || value === '1' || value.toLowerCase().includes('in') || value.toLowerCase().includes('available')) {
          return '1';
        } else if (name.includes('.0') || value === '0' || value.toLowerCase().includes('out') || value.toLowerCase().includes('unavailable')) {
          return '0';
        }
        return value;
      }
    }

    return null;
  }

  // ── Combined Filtering ───────────────────────────────────────────

  applyVariantFiltering() {
    this._lastFilterTime = Date.now();

    const availabilityValue = this.getAvailabilityFilterValue();
    const variantItems = this.productGrid.querySelectorAll('li.grid__item[data-variant-available]');

    if (!variantItems.length) {
      setTimeout(() => this.applyVariantFiltering(), 200);
      return;
    }

    // Collect selected values for each dynamic filter
    const filterSelections = this.filterConfigs.map((config) => ({
      key: config.key,
      selected: this.getSelectedValues(config.key)
    }));

    // Reset all items
    variantItems.forEach((item) => {
      item.style.display = '';
    });

    // Apply filters (AND between filters, OR within each filter)
    variantItems.forEach((item) => {
      let shouldShow = true;

      // Availability filter
      if (shouldShow && availabilityValue !== null) {
        const isAvailable = item.getAttribute('data-variant-available') === 'true';
        const valueStr = String(availabilityValue).toLowerCase().trim();

        if (valueStr === '1' || valueStr === 'true' || valueStr.includes('in') || valueStr.includes('available')) {
          shouldShow = isAvailable;
        } else if (valueStr === '0' || valueStr === 'false' || valueStr.includes('out') || valueStr.includes('unavailable')) {
          shouldShow = !isAvailable;
        }
      }

      // Dynamic filters
      for (const { key, selected } of filterSelections) {
        if (!shouldShow) break;
        if (selected.length > 0) {
          const itemVal = item.getAttribute(`data-variant-filter-${key}`);
          if (itemVal) {
            shouldShow = selected.includes(itemVal);
          }
        }
      }

      if (!shouldShow) {
        item.style.display = 'none';
      }
    });

    this.updateFilterCounts(variantItems, availabilityValue, filterSelections);
  }

  updateFilterCounts(variantItems, availabilityValue, filterSelections) {
    // Update availability counts (considering all dynamic filters)
    let inStockCount = 0;
    let outOfStockCount = 0;

    variantItems.forEach((item) => {
      const isAvailable = item.getAttribute('data-variant-available') === 'true';

      // Check if item passes all dynamic filters
      let passesAllDynamic = true;
      for (const { key, selected } of filterSelections) {
        if (selected.length > 0) {
          const itemVal = item.getAttribute(`data-variant-filter-${key}`);
          if (itemVal && !selected.includes(itemVal)) {
            passesAllDynamic = false;
            break;
          }
        }
      }

      if (passesAllDynamic) {
        if (isAvailable) inStockCount++;
        else outOfStockCount++;
      }
    });

    this.updateAvailabilityFilterLabels(inStockCount, outOfStockCount);

    // Update counts for each dynamic filter (cross-counting: each filter count
    // reflects items passing ALL OTHER filters including availability)
    for (const config of this.filterConfigs) {
      this.updateSingleFilterCounts(config.key, variantItems, availabilityValue, filterSelections);
    }
  }

  updateAvailabilityFilterLabels(inStockCount, outOfStockCount) {
    const availabilityFilter = this.findAvailabilityFilter();
    if (!availabilityFilter) return;

    const filterLabels = availabilityFilter.querySelectorAll('.facet-checkbox__text, .mobile-facets__label');
    filterLabels.forEach((label) => {
      const text = (label.textContent || label.innerText || '').trim();
      const lowerText = text.toLowerCase();
      if (lowerText.includes('in stock') || lowerText.includes('available')) {
        const updatedText = text.replace(/\(\d+\)/, `(${inStockCount})`);
        if (updatedText !== text) label.textContent = updatedText;
      } else if (lowerText.includes('out of stock') || lowerText.includes('unavailable')) {
        const updatedText = text.replace(/\(\d+\)/, `(${outOfStockCount})`);
        if (updatedText !== text) label.textContent = updatedText;
      }
    });
  }

  updateSingleFilterCounts(filterKey, variantItems, availabilityValue, filterSelections) {
    const inputs = this.variantGridSection.querySelectorAll(`[data-custom-filter="${filterKey}"]`);
    if (!inputs.length) return;

    const valueCounts = new Map();
    variantItems.forEach((item) => {
      const itemVal = item.getAttribute(`data-variant-filter-${filterKey}`);
      if (!itemVal) return;

      // Check availability
      let passes = true;
      if (availabilityValue !== null) {
        const isAvailable = item.getAttribute('data-variant-available') === 'true';
        const valueStr = String(availabilityValue).toLowerCase().trim();
        if (valueStr === '1' || valueStr === 'true' || valueStr.includes('in') || valueStr.includes('available')) {
          passes = isAvailable;
        } else if (valueStr === '0' || valueStr === 'false' || valueStr.includes('out') || valueStr.includes('unavailable')) {
          passes = !isAvailable;
        }
      }

      // Check all OTHER dynamic filters (not the current one)
      if (passes) {
        for (const { key, selected } of filterSelections) {
          if (key === filterKey) continue;
          if (selected.length > 0) {
            const otherVal = item.getAttribute(`data-variant-filter-${key}`);
            if (otherVal && !selected.includes(otherVal)) {
              passes = false;
              break;
            }
          }
        }
      }

      if (passes) {
        valueCounts.set(itemVal, (valueCounts.get(itemVal) || 0) + 1);
      }
    });

    inputs.forEach((input) => {
      const slug = input.value;
      const count = valueCounts.get(slug) || 0;
      const textSpan = input.closest('.facets__label').querySelector('.facet-checkbox__text');
      if (textSpan) {
        const currentText = textSpan.textContent;
        const updatedText = currentText.replace(/\(\d+\)/, `(${count})`);
        if (updatedText !== currentText) textSpan.textContent = updatedText;
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new VariantAvailabilityFilter();
  });
} else {
  new VariantAvailabilityFilter();
}

// Re-initialize after AJAX updates
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length) {
      const hasVariantGrid = Array.from(mutation.addedNodes).some(
        (node) => node.nodeType === 1 && node.querySelector && node.querySelector('.variant-product-grid-section')
      );
      if (hasVariantGrid) {
        setTimeout(() => new VariantAvailabilityFilter(), 100);
      }
    }
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});
